import { useEffect, useState } from "react";
import { Eye, Loader2, Plus, RefreshCw, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export const ReAdSpyTab = () => {
  const [cfg, setCfg] = useState(null);
  const [ads, setAds] = useState([]);
  const [token, setToken] = useState("");
  const [pageInput, setPageInput] = useState("");
  const [busy, setBusy] = useState("");

  const loadCfg = () => api.get("/admin/realestate/ad-spy/config").then((r) => setCfg(r.data)).catch(() => setCfg({ pages: [] }));
  const loadAds = () => api.get("/admin/realestate/ad-spy/ads").then((r) => setAds(r.data.ads || [])).catch(() => {});
  useEffect(() => { loadCfg(); loadAds(); }, []);

  const saveToken = async () => {
    if (!token.trim()) return;
    try { const r = await api.put("/admin/realestate/ad-spy/config", { token: token.trim() }); setCfg(r.data); setToken(""); toast.success("Token saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const addPage = async (ours) => {
    if (!pageInput.trim()) return;
    setBusy("resolve");
    try {
      const res = await api.post("/admin/realestate/ad-spy/resolve", { page: pageInput.trim() });
      const pages = [...(cfg.pages || []), { ...res.data, ours }];
      const r = await api.put("/admin/realestate/ad-spy/config", { pages });
      setCfg(r.data); setPageInput(""); toast.success(`Added ${res.data.name}`);
    } catch (e) { toast.error(formatApiErrorDetail(e)); } finally { setBusy(""); }
  };

  const removePage = async (id) => {
    const pages = (cfg.pages || []).filter((p) => p.id !== id);
    const r = await api.put("/admin/realestate/ad-spy/config", { pages }); setCfg(r.data);
  };

  const scan = async () => {
    setBusy("scan");
    try { const r = await api.post("/admin/realestate/ad-spy/scan"); toast.success(`Found ${r.data.found} ads across ${r.data.pages_scanned} pages`); loadAds(); loadCfg(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); } finally { setBusy(""); }
  };

  const analyze = async () => {
    setBusy("analyze");
    try { const r = await api.post("/admin/realestate/ad-spy/analyze"); setCfg((c) => ({ ...c, analysis: r.data.analysis, analyzed_at: new Date().toISOString() })); toast.success("Analysis ready"); }
    catch (e) { toast.error(formatApiErrorDetail(e)); } finally { setBusy(""); }
  };

  if (!cfg) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#C9A24B]" /></div>;
  const analysis = cfg.analysis;

  return (
    <div data-testid="re-ad-spy-tab" className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl font-semibold text-[#1A1615]">Ad Spy</h2>
        <p className="mt-1 text-xs text-[#91837C]">Monitor competitors' live Facebook/Instagram ads in the UAE via the official Meta Ad Library, then turn patterns into better property ads.</p>
      </div>

      {!cfg.has_token && (
        <div className="glass rounded-2xl p-6" data-testid="re-ad-spy-token">
          <label className="text-xs font-semibold uppercase tracking-[0.15em] text-[#B38B2D]">Meta access token</label>
          <p className="mt-1 text-xs text-[#91837C]">Get a free User token from Graph API Explorer (developers.facebook.com → Tools), then paste it here.</p>
          <div className="mt-3 flex gap-2">
            <input data-testid="re-ad-spy-token-input" value={token} onChange={(e) => setToken(e.target.value)} placeholder="EAAB..." className="flex-1 rounded-xl border border-[#E6DCD2] bg-white px-4 py-3 text-sm outline-none focus:border-[#C9A24B]" />
            <button onClick={saveToken} className="rounded-xl bg-[#B38B2D] px-5 py-3 text-sm font-medium text-white hover:opacity-90">Save</button>
          </div>
        </div>
      )}

      <div className="glass rounded-2xl p-6">
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#B38B2D]">Watchlist</span>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <input data-testid="re-ad-spy-page-input" value={pageInput} onChange={(e) => setPageInput(e.target.value)} placeholder="Facebook page URL, @username or numeric Page ID" className="flex-1 rounded-xl border border-[#E6DCD2] bg-white px-4 py-3 text-sm outline-none focus:border-[#C9A24B]" />
          <button data-testid="re-ad-spy-add-competitor" onClick={() => addPage(false)} disabled={busy === "resolve"} className="inline-flex items-center gap-1.5 rounded-xl border border-[#E6DCD2] px-4 py-3 text-sm text-[#6B5E57] hover:bg-[#F6F0E8]">{busy === "resolve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Competitor</button>
          <button onClick={() => addPage(true)} disabled={busy === "resolve"} className="rounded-xl border border-[#E6DCD2] px-4 py-3 text-sm text-[#6B5E57] hover:bg-[#F6F0E8]">+ Our page</button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {(cfg.pages || []).map((p) => (
            <span key={p.id} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs ${p.ours ? "bg-[#C9A24B]/15 text-[#8A6A1F]" : "bg-[#F6F0E8] text-[#6B5E57]"}`}>
              {p.name}{p.ours ? " (ours)" : ""}
              <button onClick={() => removePage(p.id)} className="text-[#B5A79E] hover:text-red-400"><Trash2 className="h-3 w-3" /></button>
            </span>
          ))}
          {(cfg.pages || []).length === 0 && <p className="text-xs text-[#91837C]">Add at least one competitor page.</p>}
        </div>
        <div className="mt-4 flex gap-2">
          <button data-testid="re-ad-spy-scan" onClick={scan} disabled={busy === "scan"} className="inline-flex items-center gap-2 rounded-xl bg-[#B38B2D] px-5 py-2.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60">{busy === "scan" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Scan now</button>
          <button data-testid="re-ad-spy-analyze" onClick={analyze} disabled={busy === "analyze" || ads.length === 0} className="inline-flex items-center gap-2 rounded-xl border border-[#C9A24B]/40 px-5 py-2.5 text-sm text-[#6B5E57] hover:bg-[#C9A24B]/10 disabled:opacity-50">{busy === "analyze" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} AI analysis</button>
        </div>
      </div>

      {analysis && (
        <div className="glass rounded-2xl p-6" data-testid="re-ad-spy-analysis">
          <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#B38B2D]">AI analysis</span>
          <div className="mt-3 grid gap-4 md:grid-cols-2">
            {["winning_patterns", "recommendations", "our_gaps", "competitor_themes"].map((k) => (analysis[k] || []).length > 0 && (
              <div key={k}>
                <p className="text-xs font-semibold capitalize text-[#1A1615]">{k.replace(/_/g, " ")}</p>
                <ul className="mt-1.5 space-y-1">{(analysis[k] || []).map((x, i) => <li key={i} className="text-xs leading-relaxed text-[#6B5E57]">• {x}</li>)}</ul>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="glass rounded-2xl p-6">
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#B38B2D]">Tracked ads ({ads.length})</span>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {ads.slice(0, 40).map((a) => (
            <div key={a.id} data-testid={`re-ad-${a.id}`} className="rounded-xl border border-[#EFE7DD] p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-[#1A1615]">{a.page_name}</span>
                {a.is_winner && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">WINNER · {a.days_running}d</span>}
              </div>
              <p className="mt-1.5 line-clamp-3 text-xs text-[#6B5E57]">{(a.bodies || a.titles || [])[0]}</p>
              {a.snapshot_url && <a href={a.snapshot_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-[#B38B2D] hover:underline"><Eye className="h-3 w-3" /> View on Meta</a>}
            </div>
          ))}
          {ads.length === 0 && <p className="text-xs text-[#91837C]">No ads tracked yet — add pages and run a scan.</p>}
        </div>
      </div>
    </div>
  );
};
